import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { registerStoreSynchronizer } from "./utils/store-synchronizer";

export const STORAGE_KEY = "auth-store";

export type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpirationDate: number | null;
  impersonateId: string | null;
};

export type AuthActions = {
  setAccessToken: (accessToken: string) => void;
  setAuth: (authResponse: AuthState) => void;
  setImpersonateId: (impersonateId: string | null) => void;
  reset: () => void;
};

const defaultInitialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  accessTokenExpirationDate: null,
  impersonateId: null,
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...defaultInitialState,
      setAccessToken: (accessToken) => set(() => ({ accessToken })),
      setAuth: (authResponse) => set(authResponse),
      reset: () => set(defaultInitialState),
      setImpersonateId: (impersonateId) => set(() => ({ impersonateId })),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

registerStoreSynchronizer();

export const useUserIdFromToken = (): string | null => {
  const token = useAuthStore((state) => state.accessToken);
  if (token) {
    const payload = token.split(".")[1];
    const decoded = atob(payload);
    const parsed = JSON.parse(decoded);

    return parsed.sub;
  }

  return null;
};

export const useImpersonateId = () =>
  useAuthStore((state) => state.impersonateId);

export const useSetImpersonateId = () =>
  useAuthStore((state) => state.setImpersonateId);

export { LeaveImpersonateButton } from "./components/leave-impersonate-button";

export default useAuthStore;
